import React, { useRef, useState, useEffect } from 'react';
import { Search, Mic, Moon, Sun, ShoppingCart, ShieldCheck } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { db, doc, onSnapshot } from '../firebase';
import { ProfileModal } from './ProfileModal';
import { OptimizedImage } from './OptimizedImage';

interface GlobalHeaderProps {
    isDark: boolean;
    toggleTheme: () => void;
    onLogout: () => void;
}

export const GlobalHeader: React.FC<GlobalHeaderProps> = ({ isDark, toggleTheme, onLogout }) => {
    const { user, profile } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [showProfileModal, setShowProfileModal] = useState(false);

    const isAdmin = profile?.role === "admin" || user?.email === "yousefkatato66@gmail.com";
    const userAvatar = profile?.photoURL || user?.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=300";
    const userName = profile?.name || user?.displayName || "مستخدم جديد";

    const [systemSettings, setSystemSettings] = useState<any>(null);

    useEffect(() => {
        const unsub = onSnapshot(doc(db, 'config', 'system_settings'), (snap) => {
            if (snap.exists()) setSystemSettings(snap.data());
        });
        return () => unsub();
    }, []);

    const defaultLogos = {
        light: "/logo-light.png",
        dark: "/logo-dark.png"
    };

    const logoSrc = isDark
        ? (systemSettings?.logoDark || defaultLogos.dark)
        : (systemSettings?.logoLight || defaultLogos.light);

    // Don't render global header on admin, login, checkout, or cart views
    const isHiddenRoute =
        location.pathname.startsWith('/admin') ||
        location.pathname === '/checkout' ||
        location.pathname === '/login';

    if (isHiddenRoute) return null;

    const handleLogoClick = () => {
        navigate('/', { state: { view: 'home', clearModals: true } });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const headerNeonStyle = isDark
        ? 'shadow-[0_0_15px_rgba(255,255,255,0.04)] md:shadow-[0_0_25px_rgba(255,255,255,0.06)] border-white/10'
        : 'shadow-[0_0_20px_rgba(59,130,246,0.25)] md:shadow-[0_0_30px_rgba(59,130,246,0.35)] border-blue-200';

    return (
        <>
            <header className={`fixed top-[calc(1rem+env(safe-area-inset-top))] md:top-6 z-[50] left-0 right-0 mx-auto px-4 md:px-10 py-2.5 md:py-4 flex justify-between items-center border transition-all duration-700 w-[calc(100%-2rem)] md:w-[calc(100%-5rem)] rounded-[2.5rem] md:rounded-[3rem] ${headerNeonStyle} ${isDark ? 'bg-[#0f1115]/85' : 'bg-white/90'} backdrop-blur-sm md:backdrop-blur-xl`} style={{ direction: 'ltr' }}>
                <div className={`flex items-center gap-1 md:gap-3 shrink-0 transition-all duration-300 ease-in-out ${isSearchFocused ? 'opacity-0 pointer-events-none md:opacity-100 md:pointer-events-auto w-0 md:w-auto overflow-hidden md:overflow-visible' : 'opacity-100 w-auto'}`}>
                    <button
                        onClick={() => user ? setShowProfileModal(true) : navigate('/login')}
                        className={`w-10 h-10 md:w-14 md:h-14 rounded-full border shadow-sm overflow-hidden shrink-0 transition-all hover:scale-110 active:scale-90 flex items-center justify-center ${isDark ? 'bg-slate-800 border-white/10' : 'bg-white border-slate-200'}`}
                    >
                        {user ? (
                            <OptimizedImage src={userAvatar} alt="Profile" aspectRatio="1/1" />
                        ) : (
                            <div className="flex flex-col items-center justify-center">
                                <span className={`text-[8px] md:text-[10px] font-black uppercase leading-none ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>سجل</span>
                                <span className={`text-[8px] md:text-[10px] font-black uppercase leading-none ${isDark ? 'text-white' : 'text-slate-800'}`}>دخول</span>
                            </div>
                        )}
                    </button>
                    <div onClick={handleLogoClick} className="h-[44px] md:h-[64px] overflow-hidden flex items-center cursor-pointer transition-transform hover:scale-105 active:scale-95 shrink-0">
                        <OptimizedImage
                            src={logoSrc}
                            alt="Rashid Online Logo"
                            priority={true}
                            aspectRatio="2/1"
                            objectFit="contain"
                            className="h-full w-auto"
                        />
                    </div>
                </div>

                <div className={`flex justify-center items-center flex-1 px-2 md:px-6 relative z-10 transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)]`}>
                    <div onClick={() => searchInputRef.current?.focus()} className={`cursor-text flex items-center justify-center w-full transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${isSearchFocused ? 'max-w-full md:max-w-[550px]' : 'max-w-[140px] md:max-w-[300px]'}`}>
                        <div className={`w-full rounded-full flex items-center px-4 md:px-6 py-2.5 md:py-4 transition-all duration-500 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-100'} ${isSearchFocused ? 'neon-glow-active ring-2 ring-blue-500/20' : 'shadow-inner'}`} style={{ direction: 'rtl' }}>
                            <input
                                ref={searchInputRef}
                                type="text"
                                onFocus={() => setIsSearchFocused(true)}
                                onBlur={() => setIsSearchFocused(false)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && searchInputRef.current?.value) {
                                        navigate(`/search?q=${encodeURIComponent(searchInputRef.current.value)}`);
                                        searchInputRef.current.blur();
                                    }
                                }}
                                className={`bg-transparent w-full outline-none text-right font-black transition-all duration-300 ${isDark ? 'text-white' : 'text-slate-700'} text-sm md:text-xl`}
                                placeholder={isSearchFocused ? "عايز تشتري إيه من رشيد النهارده؟" : "بحث ذكي..."}
                            />
                            <Search className={`shrink-0 transition-all duration-500 ${isSearchFocused ? 'text-blue-500 scale-110 rotate-12' : 'text-slate-400'} w-5 h-5 md:w-7 md:h-7 mr-2`} />
                        </div>
                    </div>
                </div>

                <div className={`flex items-center gap-2 md:gap-5 shrink-0 transition-all duration-300 ease-in-out ${isSearchFocused ? 'opacity-0 pointer-events-none w-0 md:w-auto md:opacity-100 md:pointer-events-auto overflow-hidden md:overflow-visible' : 'opacity-100 w-auto'}`}>
                    {isAdmin && (
                        <button
                            onClick={() => navigate('/admin')}
                            className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all active:scale-95 hover:scale-110 border shadow-sm ${isDark ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-red-50 text-red-600 border-red-100'} shrink-0`}
                            title="Admin Dashboard"
                        >
                            <ShieldCheck size={20} className="md:w-6 md:h-6" />
                        </button>
                    )}
                    <div className={`rounded-full flex items-center p-1 md:p-1.5 border transition-colors shadow-sm ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-100'} shrink-0`}>
                        <button onClick={toggleTheme} className={`${isDark ? 'bg-indigo-600' : 'bg-yellow-400'} w-8 h-8 md:w-11 md:h-11 rounded-full shadow-inner transition-all transform hover:scale-110 flex items-center justify-center shrink-0`}>
                            {isDark ? <Sun size={18} className="text-white fill-white" /> : <Moon size={18} className="text-blue-900 fill-blue-900" />}
                        </button>
                    </div>
                </div>
            </header>

            <ProfileModal
                isOpen={showProfileModal}
                onClose={() => setShowProfileModal(false)}
                isDark={isDark}
                userAvatar={userAvatar}
                userName={userName}
                userEmail={profile?.email}
                userPhone={profile?.phone}
                onLogout={onLogout}
            />
        </>
    );
};
